import { z } from "zod";

import { formatPhoneNumber, northAmericanPhoneNumberSchema } from "@/shared/utils/phone-utils";

const faxNumberSchema = northAmericanPhoneNumberSchema.transform(formatPhoneNumber);

export const ringCentralIdSchema = z
  .union([z.string().regex(/^\d+$/), z.number().int().nonnegative()])
  .transform(String);

export const ringCentralRoutingContextSchema = z
  .object({
    customerId: z.string().uuid(),
    faxNumber: z.string().nullish(),
    extensionId: ringCentralIdSchema.optional(),
  })
  .refine((routingContext) => Boolean(routingContext.faxNumber) !== Boolean(routingContext.extensionId), {
    message: "RingCentral routing context requires either a fax number or an extension ID",
  });

export type RingCentralRoutingContext = z.infer<typeof ringCentralRoutingContextSchema>;

const ringCentralServiceUserSchema = z.object({
  id: z.string().min(1),
  accountId: ringCentralIdSchema,
  customerId: z.string().uuid(),
  extensionId: ringCentralIdSchema,
  outboundFaxNumber: faxNumberSchema,
  mailboxes: z.array(
    z.object({
      extensionId: ringCentralIdSchema,
      faxNumbers: z.array(faxNumberSchema).min(1),
      groupId: z.string().uuid(),
    })
  ),
});

export type RingCentralServiceUser = z.infer<typeof ringCentralServiceUserSchema>;

function validateServiceUserAssignments(users: RingCentralServiceUser[], context: z.RefinementCtx): void {
  const userIds = new Set<string>();
  const userExtensions = new Set<string>();
  const mailboxOwnerById = new Map<string, string>();
  const groupCustomers = new Map<string, string>();
  const customerAccounts = new Map<string, string>();
  const faxNumberExtensions = new Map<string, string>();

  for (const user of users) {
    const userExtension = `${user.accountId}/${user.extensionId}`;

    if (userIds.has(user.id) || userExtensions.has(userExtension)) {
      context.addIssue({ code: "custom", message: "Service users must have distinct IDs and extensions" });
    }

    userIds.add(user.id);
    userExtensions.add(userExtension);

    const customerAccount = customerAccounts.get(user.customerId);

    if (customerAccount && customerAccount !== user.accountId) {
      context.addIssue({ code: "custom", message: "A customer must use one RingCentral account" });
    }

    customerAccounts.set(user.customerId, user.accountId);

    for (const mailbox of [
      { extensionId: user.extensionId, faxNumbers: [user.outboundFaxNumber] },
      ...user.mailboxes,
    ]) {
      for (const faxNumber of mailbox.faxNumbers) {
        const key = `${user.accountId}/${faxNumber}`;
        const extensionId = faxNumberExtensions.get(key);

        if (extensionId && extensionId !== mailbox.extensionId) {
          context.addIssue({ code: "custom", message: "A fax number must identify one RingCentral extension" });
        }

        faxNumberExtensions.set(key, mailbox.extensionId);
      }
    }

    for (const mailbox of user.mailboxes) {
      const mailboxId = `${user.accountId}/${mailbox.extensionId}`;
      const groupCustomer = groupCustomers.get(mailbox.groupId);

      if (mailboxOwnerById.has(mailboxId)) {
        context.addIssue({ code: "custom", message: "Each mailbox must have one subscribing service user" });
      }

      if (groupCustomer && groupCustomer !== user.customerId) {
        context.addIssue({ code: "custom", message: "A group cannot belong to multiple customers" });
      }

      mailboxOwnerById.set(mailboxId, user.id);
      groupCustomers.set(mailbox.groupId, user.customerId);
    }
  }

  for (const user of users) {
    const mailboxOwner = mailboxOwnerById.get(`${user.accountId}/${user.extensionId}`);

    if (mailboxOwner && mailboxOwner !== user.id) {
      context.addIssue({
        code: "custom",
        message: "A service user's extension cannot be another service user's mailbox",
      });
    }
  }
}

export const ringCentralServiceUsersSchema = z
  .array(ringCentralServiceUserSchema)
  .superRefine(validateServiceUserAssignments);
